import { useRef, useState } from "react";
import classnames from "classnames";

import styles from "./BadgeInput.module.scss";

// Reusable chip/badge multi-value input.
// - The stored value is a single comma-separated string (CSV); badges are
//   derived by splitting on commas (each segment trimmed).
// - Typing a value and pressing Enter or comma creates a badge.
// - A badge is removable via its × button or Backspace on an empty input.
// - A comma is the separator and can never appear inside a single badge;
//   spaces ARE allowed inside a badge (multi-word phrases / full emails stay
//   intact).
// - On change the badges are re-joined into the CSV string for the parent.

const splitBadges = (csv) =>
  // Tolerate a non-string value (e.g. an array) without crashing; callers pass
  // a comma-separated string, but this guards against unexpected shapes.
  (typeof csv === "string" ? csv : "")
    .split(",")
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);

const joinBadges = (badges) => badges.join(",");

const BadgeInput = ({
  id,
  value,
  onChange,
  placeholder = "",
  validateBadge,
  maxBadges,
  disabled = false,
  ariaLabel,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  const badges = splitBadges(value);

  const commitValue = (raw) => {
    const tokens = raw
      .split(",")
      .map((token) => token.trim())
      .filter((token) => token.length > 0);

    if (tokens.length === 0) {
      setInputValue("");
      return;
    }

    const next = [...badges];
    let nextError = null;
    let leftover = "";

    for (const token of tokens) {
      if (next.includes(token)) {
        // Silently drop exact duplicates.
        continue;
      }
      if (typeof maxBadges === "number" && next.length >= maxBadges) {
        nextError = `Maximum ${maxBadges} ${maxBadges === 1 ? "entry" : "entries"} allowed`;
        leftover = token;
        break;
      }
      const validationMessage = validateBadge ? validateBadge(token) : null;
      if (validationMessage) {
        nextError = validationMessage;
        leftover = token;
        break;
      }
      next.push(token);
    }

    setError(nextError);
    setInputValue(leftover);

    if (next.length !== badges.length) {
      onChange(joinBadges(next));
    }
  };

  const removeBadge = (index) => {
    const next = badges.filter((_, badgeIndex) => badgeIndex !== index);
    setError(null);
    onChange(joinBadges(next));
  };

  const handleInputChange = (event) => {
    const raw = event.target.value;
    if (raw.includes(",")) {
      // Handles pasted comma-separated content.
      commitValue(raw);
      return;
    }
    setInputValue(raw);
    if (error) {
      setError(null);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commitValue(inputValue);
      return;
    }
    if (event.key === "Backspace" && inputValue === "" && badges.length > 0) {
      event.preventDefault();
      removeBadge(badges.length - 1);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim().length > 0) {
      commitValue(inputValue);
    }
  };

  const focusInput = () => {
    if (!disabled && inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className={styles.wrapper}>
      <div
        className={classnames(styles.control, { [styles.disabled]: disabled })}
        onClick={focusInput}
      >
        {badges.map((badge, index) => (
          <span key={`${badge}-${index}`} className={styles.badge}>
            <span className={styles.badgeText}>{badge}</span>
            <button
              type="button"
              className={styles.removeButton}
              onClick={() => removeBadge(index)}
              aria-label={`Remove ${badge}`}
              disabled={disabled}
            >
              ×
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          id={id}
          type="text"
          className={styles.input}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={badges.length === 0 ? placeholder : ""}
          disabled={disabled}
          aria-label={ariaLabel}
        />
      </div>
      {error && <div className={styles.error}>{error}</div>}
    </div>
  );
};

export default BadgeInput;
